import { lazy, Suspense } from 'react'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AnimatePresence } from 'framer-motion'
import { ProtectedRoute } from '@/components/auth'
import MainLayout from '@/components/layout/MainLayout'
import { PageErrorBoundary } from '@/components/PageErrorBoundary'
import { CommandPalette } from '@/components/ui/CommandPalette'
import { ThemeProvider } from '@/contexts/ThemeProvider'
import { I18nProvider } from '@/i18n'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/pages/auth/login'))
const SignupPage = lazy(() => import('@/pages/auth/signup'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password'))
const DemoPreviewPage = lazy(() => import('@/pages/demo-preview'))
const ClassificationPage = lazy(() => import('@/pages/classification'))
const AuditLogPage = lazy(() => import('@/pages/audit-log'))
const ServiceGuidePage = lazy(() => import('@/pages/service-guide'))
const PaymentMethodsPage = lazy(() => import('@/pages/payment-methods'))
const SettingsPage = lazy(() => import('@/pages/settings'))
// 우리 탭: owner 모델 재해석(FamilyTab). 구 family 멀티계정 대시보드(@/pages/family)는
// owner 모델 채택으로 미노출 — 인프라는 보존하되 라우트에서 분리.
const FamilyTab = lazy(() => import('@/pages/redesign/FamilyTab'))
const BudgetPage = lazy(() => import('@/pages/budget'))
const NotificationsPage = lazy(() => import('@/pages/notifications'))
const HelpPage = lazy(() => import('@/pages/help'))
const CashFlowPage = lazy(() => import('@/pages/cash-flow'))
const BalanceSheetPage = lazy(() => import('@/pages/balance-sheet'))
const FinancialHealthPage = lazy(() => import('@/pages/financial-health'))

// Onboarding + Legal
const OnboardingPage = lazy(() => import('@/pages/onboarding'))
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'))
const TermsOfServicePage = lazy(() => import('@/pages/terms-of-service'))

// Redesign preview routes
const RedesignLayout = lazy(() => import('@/pages/redesign/Layout'))
const RedesignDashboard = lazy(() => import('@/pages/redesign/Dashboard'))
const RedesignUpload = lazy(() => import('@/pages/redesign/Upload'))
const RedesignReview = lazy(() => import('@/pages/redesign/Review'))
const RedesignReports = lazy(() => import('@/pages/redesign/Reports'))
const RedesignForecast = lazy(() => import('@/pages/redesign/Forecast'))
const RedesignAsk = lazy(() => import('@/pages/redesign/Ask'))
const RedesignTransactions = lazy(() => import('@/pages/redesign/Transactions'))
const RedesignNotifications = lazy(() => import('@/pages/redesign/Notifications'))
const RedesignWeeklySession = lazy(() => import('@/pages/redesign/WeeklySession'))
const RedesignPaymentCycle = lazy(() => import('@/pages/redesign/PaymentCycle'))

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
)

// Helper to wrap pages with PageErrorBoundary
const withErrorBoundary = (Component: React.ComponentType, pageName: string): React.ComponentType => {
  const WrappedComponent = () => (
    <PageErrorBoundary pageName={pageName}>
      <Component />
    </PageErrorBoundary>
  );
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
}

// Create wrapped components
const AuditLogPageWithBoundary = withErrorBoundary(AuditLogPage, '변경 이력');
const PaymentMethodsPageWithBoundary = withErrorBoundary(PaymentMethodsPage, '결제수단');
const SettingsPageWithBoundary = withErrorBoundary(SettingsPage, '설정');
const FamilyTabWithBoundary = withErrorBoundary(FamilyTab, '우리');
const BudgetPageWithBoundary = withErrorBoundary(BudgetPage, '예산');
const NotificationsPageWithBoundary = withErrorBoundary(NotificationsPage, '알림');
const HelpPageWithBoundary = withErrorBoundary(HelpPage, '도움말');
const ServiceGuidePageWithBoundary = withErrorBoundary(ServiceGuidePage, '서비스 가이드');
const ClassificationPageWithBoundary = withErrorBoundary(ClassificationPage, '분류 시스템');
const CashFlowPageWithBoundary = withErrorBoundary(CashFlowPage, '현금흐름표');
const BalanceSheetPageWithBoundary = withErrorBoundary(BalanceSheetPage, '재무상태표');
const FinancialHealthPageWithBoundary = withErrorBoundary(FinancialHealthPage, '재무건전성');

function AppRoutes() {
  const location = useLocation();
  usePushNotifications();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* 가입 없이 둘러보기 (정본 13) — 인증 불필요 공개 라우트 */}
        <Route path="/demo" element={<DemoPreviewPage />} />

        {/* ── RedesignLayout — 메인 라우트 ── */}
        <Route
          element={
            <ProtectedRoute>
              <RedesignLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<RedesignDashboard />} />
          <Route path="/forecast" element={<RedesignForecast />} />
          <Route path="/ask" element={<RedesignAsk />} />
          <Route path="/upload" element={<RedesignUpload />} />
          <Route path="/review" element={<RedesignReview />} />
          <Route path="/reports" element={<RedesignReports />} />
          <Route path="/transactions" element={<RedesignTransactions />} />
          <Route path="/notifications" element={<RedesignNotifications />} />
          <Route path="/weekly-session" element={<RedesignWeeklySession />} />
          <Route path="/payment-cycle" element={<RedesignPaymentCycle />} />
          {/* 우리(가족)·규칙·예산: RedesignLayout 크롬으로 통일 */}
          <Route path="/family" element={<FamilyTabWithBoundary />} />
          <Route path="/classification" element={<ClassificationPageWithBoundary />} />
          <Route path="/budget" element={<BudgetPageWithBoundary />} />
          {/* 변경 이력(감사): MainLayout → RedesignLayout 크롬으로 이관 */}
          <Route path="/audit-log" element={<AuditLogPageWithBoundary />} />
          {/* 디자인 통일: 돈 관리·설정 페이지를 D-시스템 크롬으로 이관 */}
          <Route path="/payment-methods" element={<PaymentMethodsPageWithBoundary />} />
          <Route path="/settings" element={<SettingsPageWithBoundary />} />
          <Route path="/cash-flow" element={<CashFlowPageWithBoundary />} />
          <Route path="/balance-sheet" element={<BalanceSheetPageWithBoundary />} />
          <Route path="/financial-health" element={<FinancialHealthPageWithBoundary />} />
          {/* 도움말·가이드: 모바일 메뉴에서 진입하므로 리디자인 크롬 안에 둔다 */}
          <Route path="/help" element={<HelpPageWithBoundary />} />
          <Route path="/service-guide" element={<ServiceGuidePageWithBoundary />} />
        </Route>

        {/* ── MainLayout — 보조 라우트 ── */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="classification-status" element={<Navigate to="/classification?tab=status" replace />} />
          <Route path="categories" element={<Navigate to="/classification?tab=categories" replace />} />
          <Route path="rules" element={<Navigate to="/classification?tab=rules" replace />} />
          {/* payment-methods·settings·cash-flow·balance-sheet·financial-health → RedesignLayout 이관(디자인 통일) */}
          {/* A3: /notifications는 RedesignLayout의 리액션 피드로 개편 (구 페이지는 legacy-notifications로 보존) */}
          <Route path="legacy-notifications" element={<NotificationsPageWithBoundary />} />
        </Route>

        {/* ── /redesign/* 하위 호환 리다이렉트 ── */}
        <Route path="/redesign" element={<Navigate to="/dashboard" replace />} />
        <Route path="/redesign/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/redesign/upload" element={<Navigate to="/upload" replace />} />
        <Route path="/redesign/review" element={<Navigate to="/review" replace />} />
        <Route path="/redesign/reports" element={<Navigate to="/reports" replace />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <AppRoutes />
        </Suspense>

        <CommandPalette />

        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#fff',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            ariaProps: {
              role: 'status',
              'aria-live': 'polite',
            },
            success: {
              style: {
                background: '#10b981',
                color: '#fff',
              },
            },
            error: {
              style: {
                background: '#ef4444',
                color: '#fff',
              },
              ariaProps: {
                role: 'alert',
                'aria-live': 'assertive',
              },
            },
          }}
        />
      </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  )
}

export default App
